import type { FromBaseEntryResult } from './from-base-entry';
import type { FromBaseRow as Row } from './from-base-context';
import { cpiDefinitionIds, cpiFoldDeps } from '../cpi/heal';
import { collectPassiveIntegerContributions, applyPassiveIntegerFold, stripPassiveIntegerFold, CPI_CAPACITY_FAMILY,
    CPI_JUMP_FAMILY, CPI_FOLD_FAMILIES, resolveJump } from './passive-integer-fold';
import { createFormulaReadContext } from './formula-read-context';
import { decideWeightEncumbrance, heldWeightEncumbrance, WEIGHT_ENCUMBRANCE_STATUS_IDS } from './creature-carrying-capacity';

const record = (value: unknown): value is Row => value !== null && typeof value === 'object' && !Array.isArray(value);
const FROM_BASE_PASSIVE_INTEGER_FOLD_STAGES = ['passive-integer-fold', 'passive-integer-fold-unfolded'];

/** Last full-compile window, after normalization has finalized stamps, level
 * gates, equipped counts and every rule cluster. Identity-bearing catalog
 * definitions without layer buckets have empty rules after normalization.
 * Other families can complete explicitly UNFOLDED. Capacity is a required final
 * answer: a miss refuses painting/write-back under the resource/member refusal. */
export function foldPassiveIntegersFromBase(result: FromBaseEntryResult,
    definition: (id: string) => Row | null,
    capacityRefusal = 'capacity-multiplier-passive-fold', missingDefinitionIds: readonly string[] = [],
    campaign: Row | null = null): FromBaseEntryResult {
    if (result.stagesMissing.length || !record(result.computed)) return result;
    const row = result.computed, computed = row.computed;
    if (!record(computed) || !record(computed.creature)) return result;
    const definitions = new Map<string, Row | null>();
    const deleted = new Set(missingDefinitionIds.map(id => id.toUpperCase()));
    const read = (id: string): Row | null => {
        const key = id.toUpperCase();
        if (definitions.has(key)) return definitions.get(key)!;
        // The native compile confirmed this id absent. An empty identity node
        // prevents the shared collectors from mistaking deletion for unavailable
        // input, or falling back to stale inline rules. It authors no layers.
        const value = deleted.has(key) ? { items: { item_id: key }, item_layer_types: {} } : definition(key);
        // A description shell has no identity. Normalization deliberately omits
        // empty layers from real definitions (notably held statuses/passives).
        if (!value || !record(value.items) || typeof value.items.item_id !== 'string'
            || value.items.item_id.toUpperCase() !== key) { definitions.set(key, null); return null; }
        if (value.item_layer_types != null && !record(value.item_layer_types)
            && !(Array.isArray(value.item_layer_types) && value.item_layer_types.length === 0)) throw new TypeError(`definition-layers-invalid:${id}`);
        const out = value.item_layer_types == null ? { ...value, item_layer_types: {} } : value;
        definitions.set(key, out);
        return out;
    };
    // Use the same strip/collect/apply primitives as refoldPassiveIntegers, with
    // one lossless detached clone. The heal's JSON clone would conceal NaN from
    // the compile referee. Failure copies are constructed only when needed.
    const unfolded = (): Row => {
        const creature = structuredClone(computed.creature) as Row;
        stripPassiveIntegerFold(creature);
        return creature;
    };
    const diagnostics = new Set<string>(), failures = new Set<string>();
    let capacityCovered = false, capacityOwned = false;
    let needsCapacity = record(computed.creature.capacity);
    const finish = (creature: Row, reason?: string): FromBaseEntryResult => ({ ...result,
        computed: { ...row, computed: { ...computed, creature } },
        ...(capacityCovered ? { capacityOwned } : { capacityOwned: undefined }),
        stagesMissing: [...new Set([...result.stagesMissing, ...failures,
            ...(needsCapacity && !capacityCovered && !failures.size ? [capacityRefusal] : [])])],
        stagesRun: [...result.stagesRun.filter(stage => !FROM_BASE_PASSIVE_INTEGER_FOLD_STAGES.includes(stage)),
            reason ? 'passive-integer-fold-unfolded' : 'passive-integer-fold'],
        stageDiagnostics: [...(result.stageDiagnostics ?? []).filter(reason => !reason.startsWith('passive-integer-fold:')),
            ...[...diagnostics, ...(reason ? [reason] : [])].map(detail => `passive-integer-fold:${detail}`)],
    });
    try {
        // The heal refuses the whole row when ANY held passive is missing,
        // including inactive/locked entries. Check before accepting any family.
        for (const id of cpiDefinitionIds(row).passives) if (!read(id)) {
            diagnostics.add(`capacity:def-missing:${id}`);
            return finish(unfolded(), `def-missing:${id}`);
        }
        const deps = cpiFoldDeps(read, missingDefinitionIds), compileSpeeds = deps.compileSpeeds!;
        deps.compileSpeeds = (host, shield) => {
            const speeds = compileSpeeds(host, shield);
            if (!speeds.ok) diagnostics.add(`speed-shield:${speeds.reason}`);
            return speeds;
        };
        const collection = collectPassiveIntegerContributions(row, deps);
        needsCapacity ||= (collection.capacity?.factor ?? 1) !== 1;
        for (const id of collection.passiveIds) if (!read(id)) {
            diagnostics.add(`capacity:def-missing:${id}`);
            return finish(unfolded(), `def-missing:${id}`);
        }
        for (const family of ['jump', 'capacity'] as const) {
            const sources = collection[family];
            for (const id of sources?.missing ?? []) diagnostics.add(`${family}:def-missing:${id}`);
            if (sources?.incomplete && !sources.missing.length) diagnostics.add(`${family}:incomplete`);
        }
        // cpiFoldDeps preserves the heal's speed-shield exception: an unknown
        // scene condition returns ok:false and only that family stays unfolded;
        // every skipped optional family is nevertheless visible in diagnostics.
        const creature = unfolded();
        const folded = applyPassiveIntegerFold(creature, collection);
        const capacity = collection.capacity;
        capacityCovered = !!capacity && !capacity.incomplete
            && (capacity.factor === 1 || folded[CPI_CAPACITY_FAMILY] === capacity.factor);
        capacityOwned = capacity?.present === true;
        if (needsCapacity && !capacityCovered && !capacity?.incomplete) diagnostics.add('capacity:unscaled');
        const speeds = record(creature.speeds) ? creature.speeds : {};
        const jump = record(speeds.jumping) ? speeds.jumping : null;
        const walk = Number(record(speeds.walking) ? speeds.walking.total : speeds.walking) || 0;
        if (collection.jump?.rows.length && !collection.jump.incomplete && jump && walk > 0
            && !(CPI_JUMP_FAMILY in folded) && !resolveJump(Number(jump.total) || 0, walk, collection.jump.rows, collection.speedShield))
            diagnostics.add('jump:unmodelled');
        for (const family of ['skill_rolls_adv', 'skill_rolls_dis']) {
            const contribution = collection.byFamily[family];
            if (contribution?.sources.length && contribution.brace !== null && !(family in folded))
                diagnostics.add(`skill-global:braced:${family}`);
        }
        if (capacityCovered && capacityOwned) {
            const finalRow = { ...row, computed: { ...computed, creature } };
            const readers = createFormulaReadContext(deps);
            const weights = decideWeightEncumbrance({ row: finalRow, campaign, layerValue: readers.layerValue,
                layerEntries: (host, key) => Object.values(readers.layerEntries(host, key)) });
            if (weights) {
                const target = record(row.statuses) ? row.statuses.target : null;
                const statuses = (Array.isArray(target) ? target : record(target) ? Object.values(target) : []).filter(record);
                const owned = heldWeightEncumbrance(row);
                for (const [tier, id] of Object.entries(WEIGHT_ENCUMBRANCE_STATUS_IDS)) {
                    const held = statuses.filter(status => String(readers.layerValue(status, 'status_item_id') ?? '').trim().toUpperCase() === id
                        && !Number(readers.layerValue(status, 'status_deleted')));
                    const wanted = weights[tier as keyof typeof weights];
                    const mismatch = !wanted && owned[tier as keyof typeof weights].length ? 'stale' : wanted && !held.length ? 'due' : null;
                    // The fold has no tier-status writer. Keep the final tier
                    // mismatch visible without blocking an otherwise complete
                    // compile; the native PC pre-fold refusals are unchanged.
                    if (mismatch) diagnostics.add(`weight-tier-mismatch:${tier}:${mismatch}`);
                }
            }
        }
        return finish(creature);
    } catch (error) {
        // A reader used only by jump, speed or an integer family must not be
        // reported as a capacity failure. Diagnose failed families on this rare
        // error path; the normal path still collects once and clones once.
        const failed = new Set<string>();
        let capacityUnavailable = true;
        for (const family of CPI_FOLD_FAMILIES) {
            try {
                const check = collectPassiveIntegerContributions(row, cpiFoldDeps(read, missingDefinitionIds), [family]);
                if (family === CPI_CAPACITY_FAMILY) capacityUnavailable = check.capacity?.incomplete !== false;
            } catch { failed.add(family); }
        }
        if (failed.has(CPI_CAPACITY_FAMILY) || capacityUnavailable) {
            diagnostics.add('capacity:incomplete');
            if (!needsCapacity) failures.add('passive-integer-fold:reader');
        }
        else {
            for (const family of failed) failures.add(`passive-integer-fold:${family}`);
            if (!failed.size) failures.add('passive-integer-fold:reader');
        }
        return finish(unfolded(), `reader:${error instanceof Error ? error.message : String(error)}`);
    }
}
